<template lang="pug">
.registration-forms(:class="{ 'registration-forms-card': !embed }")
    generic-form(
        v-if="currentForm"
        @input="onInput"
        @change="onChange"
        @prev="onPrev"
        :key="currentForm.name"
        :config="config"
        :form="currentForm.form"
        :settings="config.settings"
        :event-id="config.event_details._id"
        :orchestrators="{ 'sessions-registration': regOrchestrator }"
        :disabled="isFormDisabled"
        :errors="errors"
        :name="currentForm.name"
        :value="values[currentForm.name]"
        :action-label="actionLabel"
        :show-prev-button="Boolean(previousForm)"
        :title="title"
        :editing="editing"
        :disable-action="disabled && !nextForm"
        portal="sign-up-root"
        :embed="embed")
</template>
<script>
// Utils
import { MARKETING_EVENTS, triggerEvent } from "@/libs/utils/marketing-events";
import SessionsRegistrationOrchestrator from '@/libs/utils/sessions-registration-orchestrator';

// Components
import GenericForm from '@/components/form-elements/GenericForm.vue';

// Identity resolution sentinel for "None of the above" selections
const NONE_OF_THE_ABOVE = '__NONE__';
const IDENTITY_RESOLUTION_MATCH_VALUE_FIELD = 'identity_resolution_match_value';

/**
 * @typedef {{ name: string, value: string }[][]} Conditions
 *
 * @typedef {object} Field
 * @prop {string} label
 * @prop {string} name
 * @prop {boolean} required
 * @prop {number} order
 * @prop {string} [kind]
 * @prop {any} [kind_options]
 * @prop {boolean} [removable]
 *
 * @typedef {object} Form
 * @prop {Field[]} fields
 * @prop {string} name
 * @prop {number} order
 * @prop {Conditions} [conditions]
 *
 * @typedef {{ name?: string, form?: Form }} FormWithName
 */

/**
 * Vue component for the registration forms.
 *
 * This component handles the registration process by rendering a series of forms
 * and submitting the form data to the server. It also handles form validation and error handling.
 *
 * @component
 * @example
 * <RegistrationForms :config="registrationConfig" :editing="false" />
 *
 * @param {Object} config - The configuration object for the registration forms.
 * @param {boolean} editing - Flag indicating whether the forms are in editing mode.
 */
export default {
    name: 'RegistrationForms',

    components: { GenericForm },

    props: {
        config: {
            type: Object,
            required: true
        },

        editing: {
            type: Boolean,
            default: false
        },

        disabled: {
            type: Boolean,
            default: false
        },

        embed: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            /** @type {FormWithName} */
            currentForm: null,
            nextForm: {},
            previousForm: undefined,
            errors: null,
            forms: [],
            values: {},
            regOrchestrator: new SessionsRegistrationOrchestrator(this.$services, this.config, this.$i18n.locale),
            formDisabled: this.disabled,
            identityResolutionKey: null,
            changeInProgress: false
        };
    },

    watch: {
        currentForm(newForm, oldForm) {
            if (oldForm) {
                this.$emit('form-change');
                this.$nextTick(() => {
                    if(this.$el){
                        this.$el.scrollTop = 0;
                    }
                });
            }
        }
    },

    computed: {
        /** @returns {string} */
        actionLabel() {
            const completeKey = 'registration.actions.complete_registration';
            const continueKey = 'registration.actions.continue_registration';
            const updateKey = 'registration.actions.update_registration';
            const submitKey = this.editing ? updateKey : completeKey;

            return this.nextForm ? continueKey : submitKey;
        },

        /** @returns {boolean} */
        isProfileEnrichmentEnabled() {
            return this.config.settings.profile_enrichment === true;
        },

        /** @returns {string} */
        supportUrl() {
            const url = new URL(process.env.supportFormUrl);
            url.searchParams.append('eid', this.config.event_details._id);
            return url.toString();
        },

        /** @returns {boolean} */
        isFormDisabled() {
            return this.disabled || this.formDisabled;
        },

        /** @returns {string | undefined} */
        title() {
            if (!this.embed && this.$i18n.te(`registration.form.titles.${this.currentForm.name}`)) {
                return this.$i18n.t(`registration.form.titles.${this.currentForm.name}`)
            }
        }
    },

    mounted() {
        // if limit is reached and user is RSVP pending return to main page
        const userLimitReached = this.$services.publicLogin.isUserLimitReached(this.config);
        const isRSVPAcceptedUser = this.$services.publicLogin.isRSVPAcceptedUser(this.config);
        if (userLimitReached && !isRSVPAcceptedUser) {
            const routeName = this.embed ? 'registration-embed' : 'registration';
            if (routeName !== this.$route.name) {
                this.$router.push({ name: routeName });
            }
            return;
        }

        this.init();
    },

    methods: {
        async init() {
            await this.regOrchestrator.init(Boolean(this.$route.query.debug));
            const { forms, values } = this.prepareForms();
            this.forms = forms;
            this.values = values;
            this.currentForm = forms[0];

            const userData = this.getUserData(values);
            this.nextForm = this.getAdjacentForm(forms, this.currentForm, userData, true);
            console.debug('[RegistrationForms] Initialized', this.forms, this.values);
        },

        /**
         * Prepares the registration forms.
         *
         * Creates an array of forms and an object with values by form
         * - _hidden_fields are added to the first form
         * - user data is added to values where applicable
         *
         * @returns {Object} An object containing the prepared forms and values.
         */
        prepareForms() {
            const { _hidden_fields, ...rest } = this.config.forms;
            const user = this.config.user || {};
            const main = this.hideRSVPStatus(rest);

            const { forms, values } = this.getFormsAndValues(rest, user);

            forms.sort((a, b) => (a.form.order || 0) - (b.form.order || 0));

            this.addHiddenFields(forms, _hidden_fields);

            this.setUserLocaleField(main, values);
            this.setDefaultRSVPStatus(main, values);

            return { forms, values };
        },

        /**
         * Hides the RSVP status field in the registration form.
         *
         * @param {Object} rest - The object containing the registration forms.
         * @param {Object} rest.main - The main object containing the registration form data.
         *
         * @returns {Object} - The updated main object with the RSVP status field hidden.
         */
        hideRSVPStatus(rest) {
            const main = rest.main;
            const hideRSVPStatus = !this.$services.publicLogin.isUpdating(this.config) && main?.fields.find(field => field.name === 'fp_rsvp_status');
            if (hideRSVPStatus) {
                rest.main = {
                    ...main,
                    fields: main.fields.filter(field => field.name !== 'fp_rsvp_status')
                };
            }
            return rest.main;
        },

        /**
         * Retrieves the forms and values based on the provided parameters.
         *
         * @param {Object} rest - Additional forms to be included.
         * @param {Object} user - User object containing the values to be populated in the forms.
         *
         * @returns {Object} - An object containing the forms and their corresponding values.
         */
        getFormsAndValues(rest, user) {
            const forms = [];
            const values = {};

            for (const [key, form] of Object.entries(rest)) {
                if (key === 'matching-form') {
                    if (this.isProfileEnrichmentEnabled) {
                        // store matching form and force it to be last by giving it a very large order
                        const matchingFormObj = { name: key, form: { ...form, order: Number.MAX_SAFE_INTEGER } };
                        forms.push(matchingFormObj);
                        values[key] = {};
                    }
                    // if profile enrichment is disabled, don't add the matching form at all
                    continue;
                }
                const formObj = { name: key, form };
                forms.push(formObj);
                values[key] = {};

                for (const { name, type } of form.fields) {
                    if (name in user) {
                        values[key][name] = user[name];
                    } else if (type === 'sessions') {
                        values[key][name] = this.regOrchestrator.getAllSelectedIds();
                    }
                }
            }

            return { forms, values };
        },

        /**
         * Adds hidden fields to the registration forms.
         *
         * @param {Array} forms - The array of registration forms.
         * @param {Object} _hidden_fields - The object containing the hidden fields.
         */
        addHiddenFields(forms, _hidden_fields) {
            if (_hidden_fields && Array.isArray(_hidden_fields.fields)) {
                const firstFormFieldNames = forms[0].form.fields;
                for (const field of _hidden_fields.fields) {
                    if (!firstFormFieldNames.includes(field)) {
                        forms[0].form.fields.push(field);
                    }
                }
            }
        },

        /**
         * Sets the user locale field in the main object.
         * If the 'fp_locale' field exists in the main object, it sets its value to the current locale.
         *
         * @param {Object} main - The main object.
         * @param {Object} values - The values object.
         */
        setUserLocaleField(main, values) {
            if (main?.fields.find(field => field.name === 'fp_locale')) {
                values.main.fp_locale = this.$i18n.locale;
            }
        },

        /**
         * Sets the default RSVP status for the registration form.
         * If the 'fp_rsvp_status' field is present in the 'main' object, it sets the value to 'yes'.
         * This is done to ensure that the 'fp_rsvp_status' field always has a default value of 'yes' even if it is removed from the form.
         *
         * @param {Object} main - The main object containing the form fields.
         * @param {Object} values - The values object containing the form values.
         */
        setDefaultRSVPStatus(main, values) {
            // As fp_rsvp_status is required, a default to yes is added in case it is removed from the form
            if (!this.$services.publicLogin.isPublicReg(this.config) && !main?.fields.find(field => field.name === 'fp_rsvp_status')) {
                values.main.fp_rsvp_status = 'yes';
            }
        },

        /**
         * Retrieves user data from the provided values.
         *
         * @param {Object} values - The values object containing form data.
         *
         * @returns {Object} - The user data object.
         */
        getUserData(values) {
            // self_registered is virtual: it is never a form field, but sections can be
            // conditioned on it, so it must be set before the conditions are evaluated.
            // The backend does the same in PublicLoginConfig.sanitizeUserData().
            const currentData = { self_registered: true, ...this.config.user };
            for (const form of this.forms) {
                if (this.matches(currentData, form)) {
                    Object.assign(currentData, values[form.name]);
                }
            }
            return currentData;
        },

        /**
         * Handles the input event for the registration form.
         *
         * @param {Record<string, any>} formValues - The form values object.
         */
        onInput(formValues) {
            this.values = {
                ...this.values,
                [this.currentForm.name]: formValues

            };
            this.setPrevNext();
        },

        /**
         * Handles the change event for the registration form.
         *
         * If the next form is available, it sets the current form to the next form and updates the prev/next buttons.
         * If the next form is not available, it finalizes the registration
         *
         * @async
         * @function onChange
         * @memberof RegistrationForms
         * @throws {Error} If an error occurs during the action.
         *
         * @returns {Promise<void>}
         */
        async onChange(formValues) {
            if (this.changeInProgress) {
                return;
            }
            this.changeInProgress = true;

            const values = this.getUserData(this.values);

            // First check if the user exists if the entered email is not the same as the one in the query
            const email = this.$route.query.email;
            const isBackFromPinPage = this.$route.query._source === 'pin';
            // Only trigger login action check when there's no query email, user explicitly changed the email, or returning from pin page
            const hasQueryEmail = Boolean(email);
            const userChangedEmail = values.email != null && email !== values.email;
            if (!hasQueryEmail || userChangedEmail || isBackFromPinPage) {
                const action = await this.$services.publicLogin.getLoginAction(
                    this.config.event_details._id,
                    values.email,
                    this.$route.params.branding,
                );
                if (action === 'pin') {
                    this.$router.push({
                        name: `registration-pin${this.embed ? '-embed' : ''}`,
                        query: {
                            ...this.$route.query,
                            email: values.email,
                            _source: 'reg-form',
                        }
                    });
                    return;
                }
                if (action === 'forbidden' || (action !== 'pin' && action !== 'form')) {
                    this.handleRegistrationError({ response: { data: { name: 'RegistrationForbiddenError' }}});
                    return;
                }
            }

            try {
                this.formDisabled = true;

                if (this.nextForm?.name === 'matching-form' && this.isProfileEnrichmentEnabled && await this.handleIdentityResolution(values)) {
                    this.nextForm = null;
                }

                if (this.nextForm) {
                    this.currentForm = this.nextForm;
                    this.setPrevNext();
                    this.reconcileFields(this.previousForm);
                } else {
                    this.errors = null;
                    await this.finalizeRegistration(values);
                }
            } catch (error) {
                console.error('[RegistrationForms] Could not perform the action:', error.message);
                this.handleRegistrationError(error);
            } finally {
                this.formDisabled = false;
                this.changeInProgress = false;
            }
        },

        /**
         * Handles finalization of registration by submiting the form and redirecting if necessary
         * @param {Object} values - The form values.
         * @returns {Promise<void>}
         */
        async finalizeRegistration(values) {
            const payload = { ...values };

            this.attachIdentityResolutionKey(payload);

            const data = await this.submitRegistration(payload);
            console.info('[RegistrationForms] Action success:', data);

            this.handleFinalizeRegistrationResponse(data);
        },

        /**
         * Adds identity resolution key to the payload if present.
         * Mutates the provided payload.
         * @param {Record<string, any>} payload
         */
        attachIdentityResolutionKey(payload) {
            if (this.identityResolutionKey) {
                payload.identity_resolution_key = this.identityResolutionKey;
            }
        },

        /**
         * Handles the backend response after finalizing registration.
         * @param {any} data
         */
        handleFinalizeRegistrationResponse(data) {
            const { action, sendingEmail, redirect } = data;

            triggerEvent(MARKETING_EVENTS.FORM_SUBMIT, this.config?.event_details?.eid);

            if (redirect) {
                this.$services.routing.redirect(redirect);
                return;
            }

            const route = this.embed ? 'registration-end-embed' : 'registration-end';
            this.$router.push({
                name: route,
                params: {
                    branding: this.$route.params.branding,
                    id: this.$route.params.id,
                    end: action,
                    emailSent: sendingEmail ? '1' : '0',
                }
            });
        },

        /**
         * Submits the registration form.
         *
         * @param {Object} values - The form values.
         *
         * @returns {Promise} - A promise that resolves when the registration is submitted.
         */
        async submitRegistration(values) {
            // avoid sending full session objects, they're not used on the backend side and descriptions can be large
            delete values.registeredSessions;

            console.info('[RegistrationForms] Submitting', values);

            const { branding } = this.$route.params;
            const eventId = this.config.event_details._id;
            return await this.$services.publicLogin.registerAccount(branding, eventId, values);
        },

        /**
         * Handles the registration error.
         *
         * @param {import('axios').AxiosError} error - The error object.
         */
        handleRegistrationError(error) {
            if (error.response) {
                const { status, data } = error.response;
                console.error('[RegistrationForms] An error occurred', status, data);

                const ERROR_MAP = {
                    DocumentLimitExceededError: 'registration.form.messages.user_limit_exceeded',
                    RegistrationForbiddenError: 'registration.form.private_reg_forbidden_text_no_link',
                };

                if (ERROR_MAP[data.name]) {
                    this.errors = ERROR_MAP[data.name];
                } else if (status === 422) {
                    this.resetSessionRegistrationsSelection(data);
                    this.errors = data;
                } else {
                    this.errors = error.message;
                }
            }
        },

        /**
         * Go to the previous form in the registration process.
         */
        onPrev() {
            if (this.previousForm) {
                this.currentForm = this.previousForm;
                this.setPrevNext();
                this.reconcileFields(this.nextForm);
            }
        },

        /**
         * Sets the previous and next form based on the current form and user data.
         */
        setPrevNext() {
            const userData = this.getUserData(this.values);
            this.nextForm = this.getAdjacentForm(this.forms, this.currentForm, userData, true);
            this.previousForm = this.getAdjacentForm(this.forms, this.currentForm, userData, false);
        },

        /**
         * Reconciles the current fields with the same names with all
         * the previously collected info.
         *
         * @param {FormWithName} adjacent - The adjacent form object.
         */
        reconcileFields(adjacent) {
            const current = this.currentForm;

            for (const fieldC of current.form.fields) {
                if (!fieldC.name) continue;

                for (const fieldA of adjacent?.form.fields || []) {
                    if (!fieldA.name || fieldC.name !== fieldA.name ) continue;

                    const currentValue = this.values[this.currentForm.name][fieldC.name];
                    const adjacentValue = this.values[adjacent.name][fieldA.name];

                    if (adjacentValue !== currentValue) {
                        console.debug(`[RegistrationForms] Reconciling ${current.name}.${fieldC.name} to ${adjacent.name}.${fieldA.name}`);
                        this.$set(this.values[this.currentForm.name], fieldC.name, adjacentValue);
                    }
                }
            }
        },

        /**
         * Retrieves the next or previous form from the given array of forms based on the current form and main values.
         *
         * @param {FormWithName[]} forms - The array of forms.
         * @param {FormWithName} currentForm - The current form object.
         * @param {Record<string,any>} mainValues - The main values object.
         * @param {boolean} isNext - Indicates whether to retrieve the next form (true) or the previous form (false).
         *
         * @returns {FormWithName | undefined} - The next or previous form object, or undefined if not found.
         */
        getAdjacentForm(forms, currentForm, mainValues, isNext) {
            const currentIndex = forms.map(d => d.name).indexOf(currentForm.name);
            let adjacentForm;
            const increment = isNext ? 1 : -1;
            for (let i = currentIndex + increment; isNext ? i < forms.length : i >= 0; i += increment) {
                if (this.matches(mainValues, forms[i])) {
                    adjacentForm = forms[i];
                    break;
                }
            }
            return adjacentForm;
        },

        /**
         * Checks if the "main" form match the given conditions.
         *
         * @param {Record<string,any>} mainValues - The main values to compare.
         * @param {FormWithName} form - The form object containing the conditions.
         *
         * @returns {boolean} - Returns true if the mainValues match the conditions, otherwise false.
         */
        matches(mainValues, { form: { conditions } }) {
            if (!conditions) { return true }
            return Boolean(conditions.find(
                condition => condition.every(({ name, value }) => mainValues[name] === value)
            ))
        },

        /**
         * Resets the session registrations selection.
         *
         * @param {Object} errorData - The error data.
         */
        resetSessionRegistrationsSelection(errorData) {
            const selectedIds = this.regOrchestrator.restoreOriginalSelection(true, errorData);

            for (const formConfig of this.forms) {
                const { name, form } = formConfig;

                for (const block of form.fields.filter(field => field.type === 'sessions')) {
                    if (!errorData.hasOwnProperty(block.name)) {
                        continue;
                    }

                    this.values[name][block.name] = [];

                    for (const id of selectedIds) {
                        if (block.fp_ext_ids.includes(id)) {
                            this.values[name][block.name].push(id);
                        }
                    }
                }
            }
        },

        /**
         * Handles the identity resolution decision flow.
         * Returns true if registration was finalized early (no need to proceed to matching form).
         * @param {Object} values merged user values.
         * @returns {Promise<boolean>} whether the flow finalized the registration.
         */
        async handleIdentityResolution(values) {
            if (values.fp_rsvp_status === "no") {
              return true; // avoid triggering if rsvp response is no
            }

            // Trigger profile lookup only if at least one of the configured trigger fields is empty
            const triggerFields = this.config.settings.integration_trigger_fields || [];
            const fieldsToMatch = this.config.settings.integration_match_fields || [];

            if (fieldsToMatch.length === 0) {
              return true; // there is no match field configured
            }

            let matchesFilledComplete = true;
            for(const matchField of fieldsToMatch) {
              if (!values[matchField]) {
                matchesFilledComplete = false;
                break;
              }
            }
            if (matchesFilledComplete) {
                // Match fields are filled -> skip identity resolution and the matching form entirely
                return true;
            }

            const shouldLookup = triggerFields.length === 0 || triggerFields.some(triggerField => {
                const val = values[triggerField];
                return val === undefined || val === null || (typeof val === 'string' && val.trim() === '');
            });

            if (!shouldLookup) {
                // All trigger fields are filled -> skip identity resolution and the matching form entirely
                return true;
            }

            const matchFields = this.getIdentityResolutionMatchFields();
            if (!matchFields.length) {
                console.error('[RegistrationForms] Identity resolution matching form is missing dynamic_field.fields');
                return true;
            }

            const { branding, id } = this.$route.params;
            const regUrl = await this.$services.branding.getRegistrationUrl(branding, id);
            let webAppOrigin = null;
            try {
                webAppOrigin = new URL(regUrl).origin;
            } catch (e) {
                console.error('[RegistrationForms] Could not get registration url', e);
            }
            const { key, result: identityResolutionValues } = await this.$services.publicLogin.checkIdentityResolution(this.config.event_details._id, values, webAppOrigin);
            this.identityResolutionKey = key || null;

            /** @type {Array<{ token: string, label?: string|null, match?: Record<string, any> }>} */
            const candidates = identityResolutionValues || [];

            if (!candidates.length) {
                return true;
            }

            // If only one candidate, take it as definitive and finalize
            if (candidates.length === 1) {
                values[IDENTITY_RESOLUTION_MATCH_VALUE_FIELD] = candidates[0].token;
                return true;
            }

            this.setupIdentityResolution(candidates, matchFields);
            return false;
        },

        /**
         * Sets up the identity resolution matching form with the provided values.
         *
         * @param {Object[]} values - The array of identity resolution values.
         * @param {String[]} matchFields - array of fields user will be matching itself
         */
        setupIdentityResolution(values, matchFields) {
            const matchingForm = this.forms.find(f => f.name === 'matching-form');
            if (!matchingForm) return;

            const normalizedMatchFields = Array.isArray(matchFields) ? matchFields : this.getIdentityResolutionMatchFields();
            if (!normalizedMatchFields.length) return;

            const noneLabel = this.$i18n.t('registration.form.identity_resolution.none_of_the_above') || 'None of the above';

            const placeholderIndex = matchingForm.form.fields.findIndex(
                field =>
                    (field?.type === 'dynamic_field' || matchingForm.form.configured === true) &&
                    field?.name === IDENTITY_RESOLUTION_MATCH_VALUE_FIELD
            );

            const placeholder = placeholderIndex >= 0 ? matchingForm.form.fields[placeholderIndex] : null;

            if (placeholderIndex < 0) {
                console.error('[RegistrationForms] Identity resolution matching form is missing dynamic_field placeholder');
                return;
            }

            // Remove placeholder and insert the generated choice field at the same position
            const insertAtIndex = placeholderIndex;
            matchingForm.form.fields.splice(placeholderIndex, 1);

            const fieldLabelsByName = normalizedMatchFields.reduce((acc, fieldName) => {
                acc[fieldName] = this.$utils.controls.getLabel({ name: fieldName, required: false }, this.$i18n);
                return acc;
            }, {});

            const choiceValues = {};
            const valuesOrder = {};

            const isSingleMatchField = normalizedMatchFields.length <= 1;
            const singleFieldName = normalizedMatchFields[0];

            for (const [idx, candidate] of values.entries()) {
                if (!candidate || typeof candidate !== 'object') continue;

                const key = String(candidate.token);
                const label = isSingleMatchField
                    ? (candidate.match?.[singleFieldName] || candidate.label || key)
                    : normalizedMatchFields
                        .map(fieldName => {
                            const fieldLabel = fieldLabelsByName[fieldName] || fieldName;
                            const fieldValue = candidate.match?.[fieldName] || '';
                            return fieldValue ? `${fieldLabel}: ${fieldValue}`.trim() : null;
                        })
                        .filter(Boolean) // filter out those with null values
                        .join('\n');

                choiceValues[key] = label;
                valuesOrder[key] = idx;
            }

            choiceValues[NONE_OF_THE_ABOVE] = noneLabel;
            valuesOrder[NONE_OF_THE_ABOVE] = Number.MAX_SAFE_INTEGER;

            const baseLabel = placeholder?.label ? placeholder.label.split(',').map(s => s.trim()).join(' / ') : 'Choose a match';

            const choiceField = {
                name: IDENTITY_RESOLUTION_MATCH_VALUE_FIELD,
                label: baseLabel,
                order: placeholder?.order || 0,
                kind: 'choice',
                fields: matchFields, // to stay here for a back<==>forth navigation support
                required: true,
                kind_options: {
                    searchable: true,
                    values: choiceValues,
                    values_order: valuesOrder,
                    ...(normalizedMatchFields.length === 1 ? { appearance: 'buttons', inline: true } : {})
                }
            };

            matchingForm.form.fields.splice(insertAtIndex, 0, choiceField);
            matchingForm.form.configured = true;
        },

        /**
         * Determines which fields should be used to display/map identity candidates.
         *
         * @returns {string[]}
         */
        getIdentityResolutionMatchFields() {
            const matchingForm = this.forms.find(f => f.name === 'matching-form');
            const dynamicField = matchingForm?.form?.fields?.find(
                field =>
                    (field?.type === 'dynamic_field' || matchingForm.form.configured === true) &&
                    field?.name === IDENTITY_RESOLUTION_MATCH_VALUE_FIELD
            );

            return dynamicField?.fields || [];
        },
    }
}
</script>
